<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing radius-7">
    <div class="card-head box-shadow bg-white p-15 p-sm-20 p-md-25 radius-7">
      <div class="stock-toolbar">
        <div>
          <h5 class="mb-1 fw-bold">Estoque de produtos</h5>
          <p class="mb-0 text-muted fs-14">Controle entradas, ajustes e saldo atual de cada produto.</p>
        </div>
        <div class="stock-search">
          <input
            v-model="search"
            type="text"
            class="form-control shadow-none rounded-0 text-black radius-7"
            placeholder="Buscar por nome ou SKU..."
            @input="debouncedLoadStock(1)"
          />
        </div>
      </div>
    </div>

    <div class="card-body p-15 p-sm-20 p-md-25 mt-10">
      <div v-if="feedback.message" class="alert radius-7 mb-20" :class="feedback.type === 'error' ? 'alert-danger' : 'alert-success'">
        {{ feedback.message }}
      </div>

      <div class="table-responsive">
        <table class="table text-nowrap align-middle mb-0 stock-table">
          <thead>
            <tr>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 ps-0">Produto</th>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Estoque</th>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Mínimo</th>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Status</th>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Adicionar</th>
              <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 pe-0">Definir estoque</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in products" :key="product.id">
              <td class="shadow-none lh-1 fw-medium text-black-emphasis ps-0 stock-product-cell">
                <span class="stock-product-name">{{ product.name || 'Sem nome' }}</span>
                <small class="stock-product-sku">{{ product.sku || '-' }}</small>
              </td>
              <td class="shadow-none lh-1 fw-bold text-black-emphasis">
                {{ stockQuantity(product) }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <input
                  v-model.number="stockForms[product.id].minimum_quantity"
                  type="number"
                  min="0"
                  class="form-control form-control-sm stock-number-input radius-7"
                />
              </td>
              <td class="shadow-none lh-1">
                <span class="badge radius-7" :class="stockStatus(product).className">
                  {{ stockStatus(product).label }}
                </span>
              </td>
              <td class="shadow-none lh-1">
                <div class="stock-action">
                  <input
                    v-model.number="stockForms[product.id].add_quantity"
                    type="number"
                    min="1"
                    class="form-control form-control-sm stock-number-input radius-7"
                  />
                  <button type="button" class="btn btn-sm btn-success radius-7" :disabled="loadingProductId === product.id" @click="addStock(product)">
                    Adicionar
                  </button>
                </div>
              </td>
              <td class="shadow-none lh-1 pe-0">
                <div class="stock-action">
                  <input
                    v-model.number="stockForms[product.id].set_quantity"
                    type="number"
                    min="0"
                    class="form-control form-control-sm stock-number-input radius-7"
                  />
                  <button type="button" class="btn btn-sm btn-light radius-7" :disabled="loadingProductId === product.id" @click="adjustStock(product)">
                    Salvar
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoading && products.length === 0" class="stock-empty radius-7">
        Nenhum produto encontrado para controlar estoque.
      </div>

      <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mt-20">
        <div class="fs-14 text-muted mb-10 mb-sm-0">
          Página {{ currentPage }} de {{ pagination.last_page || 1 }} — Total de {{ pagination.total || products.length }} produtos
        </div>
        <nav aria-label="Paginação de estoque">
          <ul class="pagination mb-0">
            <li class="page-item" :class="{ disabled: currentPage <= 1 }">
              <button type="button" class="page-link" @click="loadStock(currentPage - 1)" :disabled="currentPage <= 1">Anterior</button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage >= pagination.last_page }">
              <button type="button" class="page-link" @click="loadStock(currentPage + 1)" :disabled="currentPage >= pagination.last_page">Próxima</button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import api from '../../config/axios.js';

type ProductStockForm = {
  add_quantity: number;
  set_quantity: number;
  minimum_quantity: number;
};

type ProductStock = {
  quantity?: number;
  minimum_quantity?: number;
};

type StockProduct = {
  id: number;
  name?: string;
  sku?: string;
  stock?: ProductStock;
};

export default defineComponent({
  name: "ProductStockTab",
  data() {
    return {
      products: [] as StockProduct[],
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      },
      currentPage: 1,
      search: '',
      stockForms: {} as Record<number, ProductStockForm>,
      isLoading: false,
      loadingProductId: null as number | null,
      searchTimer: undefined as number | undefined,
      feedback: {
        type: 'success',
        message: ''
      }
    };
  },
  methods: {
    async loadStock(page = 1) {
      if (!page || page < 1 || (this.pagination.last_page && page > this.pagination.last_page)) return;

      this.isLoading = true;
      this.currentPage = page;

      try {
        const response = await api.axios.get('/api/products/stock', {
          params: {
            page,
            search: this.search || undefined
          }
        });
        const payload = response.data || {};
        this.products = Array.isArray(payload.data) ? payload.data.filter(Boolean) : [];
        this.pagination = {
          current_page: payload.current_page || page,
          last_page: payload.last_page || 1,
          per_page: payload.per_page || this.products.length,
          total: payload.total || this.products.length,
        };
        this.prepareForms();
      } catch (error) {
        this.products = [];
        this.showFeedback('Não foi possível carregar o estoque.', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    debouncedLoadStock(page = 1) {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      this.searchTimer = window.setTimeout(() => {
        this.loadStock(page);
        this.searchTimer = undefined;
      }, 500);
    },
    prepareForms() {
      this.products.forEach((product) => {
        const quantity = this.stockQuantity(product);
        const minimum = product.stock?.minimum_quantity || 0;

        this.stockForms[product.id] = {
          add_quantity: this.stockForms[product.id]?.add_quantity || 1,
          set_quantity: quantity,
          minimum_quantity: minimum,
        };
      });
    },
    stockQuantity(product: StockProduct) {
      return Number(product.stock?.quantity || 0);
    },
    stockStatus(product: StockProduct) {
      const quantity = this.stockQuantity(product);
      const minimum = Number(product.stock?.minimum_quantity || 0);

      if (quantity <= 0) {
        return { label: 'Sem estoque', className: 'bg-danger' };
      }

      if (minimum > 0 && quantity <= minimum) {
        return { label: 'Baixo', className: 'bg-warning text-dark' };
      }

      return { label: 'OK', className: 'bg-success' };
    },
    async addStock(product: StockProduct) {
      const form = this.stockForms[product.id];
      const quantity = Number(form?.add_quantity || 0);

      if (quantity < 1) {
        this.showFeedback('Informe uma quantidade maior que zero para adicionar estoque.', 'error');
        return;
      }

      await this.saveStock(product, 'add', {
        quantity,
        minimum_quantity: Number(form.minimum_quantity || 0),
      });
    },
    async adjustStock(product: StockProduct) {
      const form = this.stockForms[product.id];
      const quantity = Number(form?.set_quantity ?? 0);

      if (quantity < 0) {
        this.showFeedback('O estoque definido não pode ser negativo.', 'error');
        return;
      }

      await this.saveStock(product, 'adjust', {
        quantity,
        minimum_quantity: Number(form.minimum_quantity || 0),
      });
    },
    async saveStock(product: StockProduct, action: 'add' | 'adjust', payload: { quantity: number; minimum_quantity: number }) {
      this.loadingProductId = product.id;

      try {
        const response = await api.axios.post(`/api/products/${product.id}/stock/${action}`, payload);
        product.stock = response.data;
        this.stockForms[product.id].set_quantity = this.stockQuantity(product);
        this.stockForms[product.id].add_quantity = 1;
        this.showFeedback('Estoque atualizado com sucesso.', 'success');
      } catch (error) {
        this.showFeedback('Não foi possível atualizar o estoque deste produto.', 'error');
      } finally {
        this.loadingProductId = null;
      }
    },
    showFeedback(message, type = 'success') {
      this.feedback = { message, type };
      window.setTimeout(() => {
        this.feedback.message = '';
      }, 4500);
    }
  },
  mounted() {
    this.loadStock(1);
  }
});
</script>

<style scoped>
.stock-toolbar {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.stock-search {
  max-width: 360px;
  width: 100%;
}

.stock-table {
  min-width: 860px;
}

.stock-product-cell {
  max-width: 260px;
}

.stock-product-name,
.stock-product-sku {
  display: block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stock-product-sku {
  color: #6b7280;
  font-size: 12px;
  margin-top: 4px;
}

.stock-number-input {
  min-width: 86px;
  width: 86px;
}

.stock-action {
  align-items: center;
  display: flex;
  gap: 8px;
}

.stock-empty {
  background: #f8fafc;
  border: 1px dashed #dbe3ef;
  color: #6b7280;
  margin-top: 18px;
  padding: 22px;
  text-align: center;
}

@media (max-width: 767.98px) {
  .stock-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .stock-search {
    max-width: none;
  }
}
</style>
